// FLEX DSL Language Definition for Monaco Editor
// TextMate-style grammar for syntax highlighting

export const FLEX_LANGUAGE_ID = 'flex';

// Monarch token definitions for FLEX DSL
export const flexLanguageConfig = {
  comments: {
    lineComment: '//',
  },
  brackets: [
    ['{', '}'],
    ['[', ']'],
    ['(', ')'],
  ],
  autoClosingPairs: [
    { open: '{', close: '}' },
    { open: '[', close: ']' },
    { open: '(', close: ')' },
    { open: '"', close: '"' },
    { open: "'", close: "'" },
  ],
  surroundingPairs: [
    { open: '{', close: '}' },
    { open: '[', close: ']' },
    { open: '(', close: ')' },
    { open: '"', close: '"' },
    { open: "'", close: "'" },
  ],
  folding: {
    markers: {
      start: /^\s*\{/,
      end: /^\s*\}/,
    },
  },
};

// Monarch token provider for FLEX DSL
export const flexTokensProvider = {
  defaultToken: '',
  tokenPostfix: '.flex',

  keywords: [
    'if', 'else', 'for', 'in', 'parallel', 'super', 'true', 'false', 'null',
    'return', 'break', 'continue', 'while', 'do', 'switch', 'case', 'default',
  ],

  operators: [
    '=', '==', '!=', '<', '>', '<=', '>=', '+', '-', '*', '/', '%',
    '&&', '||', '!', '&', '|', '^', '~', '<<', '>>', '++', '--',
    '+=', '-=', '*=', '/=', '%=', '&=', '|=', '^=', '<<=', '>>=',
    '->', '=>', '?', ':',
  ],

  symbols: /[=><!~?:&|+\-*\/\^%]+/,
  escapes: /\\(?:[abfnrtv\\"']|x[0-9A-Fa-f]{1,4}|u[0-9A-Fa-f]{4}|U[0-9A-Fa-f]{8})/,
  digits: /\d+(_+\d+)*/,

  tokenizer: {
    root: [
      // Directives (e.g., @trace, @extends)
      [/@[a-zA-Z_]\w*/, 'keyword.directive'],

      // Comments
      [/\/\/.*$/, 'comment'],

      // Strings
      [/"([^"\\]|\\.)*$/, 'string.invalid'],
      [/"/, { token: 'string.quote', bracket: '@open', next: '@string_double' }],
      [/'([^'\\]|\\.)*$/, 'string.invalid'],
      [/'/, { token: 'string.quote', bracket: '@open', next: '@string_single' }],

      // Numbers
      [/\d+\.\d*([eE][\-+]?\d+)?/, 'number.float'],
      [/\.\d+([eE][\-+]?\d+)?/, 'number.float'],
      [/\d+[eE][\-+]?\d+/, 'number.float'],
      [/\d+/, 'number'],

      // Entity.Method pattern (e.g., Area.GetAll)
      [/([A-Z][a-zA-Z0-9]*)(\.)([A-Z][a-zA-Z0-9]*)(\()/, [
        'type.entity',
        'delimiter',
        'function.method',
        'delimiter.parenthesis',
      ]],

      // Entity reference (capitalized identifier before a dot)
      [/[A-Z][a-zA-Z0-9]*(?=\.)/, 'type.entity'],

      // Method call after dot
      [/(?<=\.)[A-Z][a-zA-Z0-9]*(?=\()/, 'function.method'],

      // Capture operator
      [/->/, 'operator.capture'],

      // Identifiers and keywords
      [/[a-zA-Z_]\w*/, {
        cases: {
          '@keywords': 'keyword',
          '@default': 'identifier',
        },
      }],

      // Delimiters and operators
      [/[{}()\[\]]/, '@brackets'],
      [/[<>](?!@symbols)/, '@brackets'],
      [/@symbols/, {
        cases: {
          '@operators': 'operator',
          '@default': '',
        },
      }],

      // Delimiter: comma, semicolon
      [/[;,]/, 'delimiter'],

      // Whitespace
      { include: '@whitespace' },
    ],

    string_double: [
      [/[^\\"]+/, 'string'],
      [/@escapes/, 'string.escape'],
      [/\\./, 'string.escape.invalid'],
      [/"/, { token: 'string.quote', bracket: '@close', next: '@pop' }],
    ],

    string_single: [
      [/[^\\']+/, 'string'],
      [/@escapes/, 'string.escape'],
      [/\\./, 'string.escape.invalid'],
      [/'/, { token: 'string.quote', bracket: '@close', next: '@pop' }],
    ],

    whitespace: [
      [/[ \t\r\n]+/, 'white'],
      [/\/\*/, 'comment', '@comment'],
      [/\/\/.*$/, 'comment'],
    ],

    comment: [
      [/[^\/*]+/, 'comment'],
      [/\/\*/, 'comment', '@push'],
      ['\\*/', 'comment', '@pop'],
      [/[\/*]/, 'comment'],
    ],
  },
};

// Theme colors for FLEX DSL (matches the app's dark theme)
export const flexThemeDark = {
  base: 'vs-dark',
  inherit: true,
  rules: [
    // Keywords - purple/violet
    { token: 'keyword', foreground: 'c084fc', fontStyle: 'bold' },
    { token: 'keyword.directive', foreground: 'a78bfa', fontStyle: 'italic' },

    // Entities - teal/cyan
    { token: 'type.entity', foreground: '22d3ee', fontStyle: 'bold' },

    // Methods/Functions - bright green
    { token: 'function.method', foreground: '4ade80' },

    // Strings - orange
    { token: 'string', foreground: 'fb923c' },
    { token: 'string.quote', foreground: 'fb923c' },
    { token: 'string.escape', foreground: 'fbbf24' },

    // Numbers - yellow
    { token: 'number', foreground: 'fbbf24' },
    { token: 'number.float', foreground: 'fbbf24' },

    // Comments - muted gray
    { token: 'comment', foreground: '6b7280', fontStyle: 'italic' },

    // Operators - light gray/white
    { token: 'operator', foreground: 'd4d4d8' },
    { token: 'operator.capture', foreground: '22c55e', fontStyle: 'bold' },

    // Identifiers - white
    { token: 'identifier', foreground: 'e5e7eb' },

    // Delimiters
    { token: 'delimiter', foreground: 'd4d4d8' },
    { token: 'delimiter.parenthesis', foreground: 'd4d4d8' },

    // Brackets
    { token: '@brackets', foreground: 'd4d4d8' },
  ],
  colors: {
    'editor.background': '#09090b',
    'editor.foreground': '#e5e7eb',
    'editor.lineHighlightBackground': '#18181b',
    'editor.selectionBackground': '#27272a',
    'editorCursor.foreground': '#22c55e',
    'editor.selectionHighlightBackground': '#3f3f46',
    'editorLineNumber.foreground': '#52525b',
    'editorLineNumber.activeForeground': '#a1a1aa',
    'editorIndentGuide.background': '#27272a',
    'editorIndentGuide.activeBackground': '#3f3f46',
    'editorBracketMatch.background': '#22c55e33',
    'editorBracketMatch.border': '#22c55e',
  },
};

// Light theme variant
export const flexThemeLight = {
  base: 'vs',
  inherit: true,
  rules: [
    // Keywords - purple
    { token: 'keyword', foreground: '7c3aed', fontStyle: 'bold' },
    { token: 'keyword.directive', foreground: '8b5cf6', fontStyle: 'italic' },

    // Entities - teal
    { token: 'type.entity', foreground: '0891b2', fontStyle: 'bold' },

    // Methods/Functions - green
    { token: 'function.method', foreground: '16a34a' },

    // Strings - orange
    { token: 'string', foreground: 'c2410c' },
    { token: 'string.quote', foreground: 'c2410c' },
    { token: 'string.escape', foreground: 'b45309' },

    // Numbers - amber
    { token: 'number', foreground: 'b45309' },
    { token: 'number.float', foreground: 'b45309' },

    // Comments - gray
    { token: 'comment', foreground: '6b7280', fontStyle: 'italic' },

    // Operators
    { token: 'operator', foreground: '374151' },
    { token: 'operator.capture', foreground: '16a34a', fontStyle: 'bold' },

    // Identifiers
    { token: 'identifier', foreground: '1f2937' },

    // Delimiters
    { token: 'delimiter', foreground: '374151' },
  ],
  colors: {
    'editor.background': '#fafafa',
    'editor.foreground': '#1f2937',
    'editor.lineHighlightBackground': '#f4f4f5',
    'editor.selectionBackground': '#e4e4e7',
    'editorCursor.foreground': '#16a34a',
    'editor.selectionHighlightBackground': '#d4d4d8',
    'editorLineNumber.foreground': '#71717a',
    'editorLineNumber.activeForeground': '#3f3f46',
    'editorIndentGuide.background': '#e4e4e7',
    'editorIndentGuide.activeBackground': '#d4d4d8',
    'editorBracketMatch.background': '#16a34a33',
    'editorBracketMatch.border': '#16a34a',
  },
};

// Register FLEX language with Monaco
export function registerFlexLanguage(monaco) {
  // Register language ID
  monaco.languages.register({ id: FLEX_LANGUAGE_ID });

  // Set language configuration
  monaco.languages.setLanguageConfiguration(FLEX_LANGUAGE_ID, flexLanguageConfig);

  // Set token provider
  monaco.languages.setMonarchTokensProvider(FLEX_LANGUAGE_ID, flexTokensProvider);

  // Define themes
  monaco.editor.defineTheme('flex-dark', flexThemeDark);
  monaco.editor.defineTheme('flex-light', flexThemeLight);
}
